import { Link } from '@tanstack/react-router'
import { ChevronRight, Mail } from 'lucide-react'
import { useMemo, useState } from 'react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { formatDayLabel } from '@/lib/dates'
import { cn } from '@/lib/utils'
import { useClientDashboard } from '@/queries/clientDashboards'
import { useNotLogged } from '@/queries/clientMeetings'
import type { CallRequest, NotLoggedEntry } from '@/types/clientMeetings'
import { CountChip } from './dashboardParts'
import { LoadError } from './LoadError'
import { AddToHintButton, DismissRequestButton, RequestActions, type RequestContext } from './RequestActions'
import { RequestFlag } from './RequestFlag'
import { RaisedBy } from './RequestsTable'
import { clientTabPath } from './tabs'
import { SOURCE_HEADER } from './sourceColours'
import { TicketRefLink } from './TicketRefLink'

const entryLabel = (entry: NotLoggedEntry) => `${formatDayLabel(entry.date)} · ${entry.title}`
const entryId = (entry: NotLoggedEntry) => (entry.kind === 'email' ? entry.emailId : entry.meetingId)

const requestText = (request: CallRequest) =>
  [request.title, request.detail, request.raisedBy, request.hint?.ref, request.hint?.title].filter(Boolean).join(' ').toLowerCase()

// Keep calls and emails whose title or date match whole, and otherwise only the requests that match.
function search(entries: NotLoggedEntry[], words: string): NotLoggedEntry[] {
  const wanted = words.trim().toLowerCase()
  if (!wanted) return entries
  return entries
    .map(entry => (entryLabel(entry).toLowerCase().includes(wanted) ? entry : { ...entry, requests: entry.requests.filter(request => requestText(request).includes(wanted)) }))
    .filter(entry => entry.requests.length)
}

function Entry({ request, context }: { request: CallRequest; context: RequestContext }) {
  return (
    <li className={cn('px-[18px] py-3', request.dismissed && 'opacity-60')}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <span className="font-medium text-etn-text">{request.title}</span>
          <RaisedBy request={request} />
        </div>
        <div className="flex items-start gap-2">
          <RequestFlag request={request} />
          <DismissRequestButton request={request} />
        </div>
      </div>
      {request.detail && <blockquote className="mt-2 border-l-2 border-etn-border-hairline pl-3 text-[13px] text-etn-text-cell">"{request.detail}"</blockquote>}
      <div className="mt-2 flex flex-wrap items-center gap-2 text-[12.5px] text-etn-text-soft">
        {request.hint ? (
          <>
            <span>
              Closest existing ticket ({request.hint.confidence}%):{' '}
              <TicketRefLink target={request.hint} className="font-medium" /> <span className="text-etn-text-cell">{request.hint.title}</span>
            </span>
            <AddToHintButton request={request} />
          </>
        ) : (
          'Nothing similar is tracked yet.'
        )}
      </div>
      <div className="mt-2">
        <RequestActions request={request} context={context} />
      </div>
    </li>
  )
}

function EntryCard({ entry, context, open, onToggle }: { entry: NotLoggedEntry; context: RequestContext; open: boolean; onToggle: () => void }) {
  // Keep a band under a collapsed card's bar, so the bar never reaches the rounded bottom corners.
  return (
    <section className={cn('overflow-hidden rounded-etn-card border border-etn-border-hairline bg-etn-surface shadow-etn-whisper', !open && 'pb-1.5')}>
      <div data-source={entry.kind} className={cn('flex items-center gap-2 px-[18px] py-3', SOURCE_HEADER[entry.kind])}>
        <button type="button" aria-expanded={open} onClick={onToggle} className="flex min-w-0 flex-1 items-center gap-2 text-left">
          <ChevronRight aria-hidden className={cn('size-4 shrink-0 text-etn-text-soft transition-transform', open && 'rotate-90')} />
          {entry.kind === 'email' && <Mail aria-label="Email" className="size-4 shrink-0 text-etn-text-soft" />}
          <span className="truncate text-[13.5px] font-semibold text-etn-text">{entryLabel(entry)}</span>
          <CountChip>{entry.requests.length}</CountChip>
        </button>
        <Link
          to={clientTabPath(context.companyId, 'meetings') as never}
          search={(entry.kind === 'email' ? { email: entry.emailId } : { meeting: entry.meetingId }) as never}
          className="shrink-0 text-[12px] font-medium text-etn-accent-text-active hover:underline"
        >
          Open on Meetings and Emails
        </Link>
      </div>
      {open && (
        <ul className="divide-y divide-etn-border-row">
          {entry.requests.map(request => (
            <Entry key={request._id} request={request} context={context} />
          ))}
        </ul>
      )}
    </section>
  )
}

// List the client's unlinked requests by call or email, newest first, each collapsible and the whole list searchable.
export function NotLoggedView({ companyId }: { companyId: string }) {
  const [showDismissed, setShowDismissed] = useState(false)
  const [words, setWords] = useState('')
  const [toggled, setToggled] = useState<Record<string, boolean>>({})
  const notLogged = useNotLogged(companyId, showDismissed)
  const dashboard = useClientDashboard(companyId).data?.data
  const entries = notLogged.data?.data
  const shown = useMemo(() => search(entries ?? [], words), [entries, words])

  if (notLogged.isError) return <LoadError title="Not logged didn't load" onRetry={() => notLogged.refetch()} />
  if (!entries || !dashboard) return null

  const searching = Boolean(words.trim())
  const isOpen = (entry: NotLoggedEntry, index: number) => toggled[entryId(entry)] ?? (searching || index === 0)
  const setAll = (open: boolean) => setToggled(Object.fromEntries(shown.map(entry => [entryId(entry), open])))
  const context = (entry: NotLoggedEntry): RequestContext => ({
    source: { kind: entry.kind, _id: entryId(entry), title: entry.title, date: entry.date },
    companyId,
    companyName: dashboard.companyName,
    trackerEnabled: dashboard.clientTrackerEnabled,
  })

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-3">
        <Input
          type="search"
          aria-label="Search not logged requests"
          placeholder="Search requests, people, calls or emails"
          value={words}
          onChange={event => {
            setWords(event.target.value)
            setToggled({})
          }}
          className="max-w-xs"
        />
        <label className="flex items-center gap-1.5 text-[12.5px] text-etn-text-cell">
          <input type="checkbox" checked={showDismissed} onChange={event => setShowDismissed(event.target.checked)} />
          Show dismissed
        </label>
        <span className="flex-1" />
        <Button size="xs" variant="ghost" onClick={() => setAll(true)}>
          Expand all
        </Button>
        <Button size="xs" variant="ghost" onClick={() => setAll(false)}>
          Collapse all
        </Button>
      </div>
      <p className="text-[12.5px] text-etn-text-soft">Requests from {dashboard.companyName}'s calls and emails that never became a ticket, newest first.</p>
      {!entries.length ? (
        <div className="rounded-etn-card border border-etn-border-hairline bg-etn-surface px-[18px] py-8 text-center">
          <p className="font-medium text-etn-text">Nothing waiting to be logged</p>
          <p className="mt-1 text-[13px] text-etn-text-soft">Requests from {dashboard.companyName}'s calls and logged emails appear here when they never became a ticket.</p>
        </div>
      ) : shown.length ? (
        shown.map((entry, index) => (
          <EntryCard
            key={entryId(entry)}
            entry={entry}
            context={context(entry)}
            open={isOpen(entry, index)}
            onToggle={() => setToggled(current => ({ ...current, [entryId(entry)]: !isOpen(entry, index) }))}
          />
        ))
      ) : (
        <p className="py-6 text-center text-[13px] text-etn-text-soft">Nothing matches "{words.trim()}"</p>
      )}
    </div>
  )
}
